'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { TweetActions } from '@/components/tweet/TweetActions';
import { useTweetActions } from '@/lib/hooks/api/social-actions';
import type { TweetMetrics, Tweet as TweetType } from '@/types/tweet';

export interface InlineTweetActionToolbarProperties {
  tweet: TweetType;
  tweetId: string;
}

type TweetActionCacheEntry = {
  metrics: TweetMetrics;
  isLiked: boolean;
  isRetweeted: boolean;
  isBookmarked: boolean;
};

export function InlineTweetActionToolbar({ tweetId, tweet }: InlineTweetActionToolbarProperties) {
  const queryClient = useQueryClient();

  const initialMetrics = useMemo<TweetMetrics>(
    () => ({
      likeCount: tweet.likeCount ?? 0,
      retweetCount: tweet.retweetCount ?? 0,
      replyCount: tweet.replyCount ?? 0,
      quoteCount: tweet.quoteCount ?? 0,
      bookmarkCount: tweet.bookmarkCount ?? 0,
      impressionCount: tweet.impressionCount ?? 0,
    }),
    [
      tweet.bookmarkCount,
      tweet.impressionCount,
      tweet.likeCount,
      tweet.quoteCount,
      tweet.replyCount,
      tweet.retweetCount,
    ]
  );

  const {
    metrics,
    isLiked,
    isRetweeted,
    isBookmarked,
    isLoading: actionsLoading,
    toggleLike,
    retweet,
    toggleBookmark,
    reply,
    share,
    copyLink,
  } = useTweetActions(tweetId, initialMetrics, tweet.author?.username ?? '');

  const handleShare = useCallback(() => {
    Promise.resolve(share()).catch((error: unknown) => {
      console.error('Failed to invoke tweet share', error);
    });
  }, [share]);

  const handleCopyLink = useCallback(() => {
    Promise.resolve(copyLink()).catch((error: unknown) => {
      console.error('Failed to copy tweet link', error);
    });
  }, [copyLink]);

  useEffect(() => {
    queryClient.setQueryData<TweetActionCacheEntry | undefined>(['tweet-actions', tweetId], (existing) => {
      const previous = existing ?? {
        metrics: {
          likeCount: tweet.likeCount ?? 0,
          retweetCount: tweet.retweetCount ?? 0,
          replyCount: tweet.replyCount ?? 0,
          quoteCount: tweet.quoteCount ?? 0,
          bookmarkCount: tweet.bookmarkCount ?? 0,
          impressionCount: tweet.impressionCount ?? undefined,
          viewCount: metrics.viewCount,
        },
        isLiked: Boolean(tweet.isLiked),
        isRetweeted: Boolean(tweet.isRetweeted),
        isBookmarked: Boolean(tweet.isBookmarked),
      };

      const nextMetrics: TweetMetrics = {
        ...previous.metrics,
        likeCount: tweet.likeCount ?? previous.metrics.likeCount ?? 0,
        retweetCount: tweet.retweetCount ?? previous.metrics.retweetCount ?? 0,
        replyCount: tweet.replyCount ?? previous.metrics.replyCount ?? 0,
        quoteCount: tweet.quoteCount ?? previous.metrics.quoteCount ?? 0,
        bookmarkCount: tweet.bookmarkCount ?? previous.metrics.bookmarkCount ?? 0,
        impressionCount:
          typeof tweet.impressionCount === 'number' ? tweet.impressionCount : previous.metrics.impressionCount,
      };
      if (typeof metrics.viewCount === 'number') {
        nextMetrics.viewCount = metrics.viewCount;
      }

      return {
        metrics: nextMetrics,
        isLiked: typeof tweet.isLiked === 'boolean' ? tweet.isLiked : previous.isLiked,
        isRetweeted: typeof tweet.isRetweeted === 'boolean' ? tweet.isRetweeted : previous.isRetweeted,
        isBookmarked: typeof tweet.isBookmarked === 'boolean' ? tweet.isBookmarked : previous.isBookmarked,
      };
    });
  }, [tweet, tweetId, metrics.viewCount, queryClient]);

  return (
    <TweetActions
      metrics={metrics}
      states={{
        isLiked,
        isRetweeted,
        isBookmarked,
        isLoading: actionsLoading,
      }}
      onReply={reply}
      onRetweet={retweet}
      onLike={toggleLike}
      onBookmark={toggleBookmark}
      onShare={handleShare}
      onCopyLink={handleCopyLink}
      enableActions
      compact
    />
  );
}
